import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getUseCasesBySlug } from '../data/useCases';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/meewwwbb';
const SESSION_PREFIX = 'db_usecase_unlocked_';
const GATE_DELAY_MS = 2500;

const css = `
  .uc-root{
    --blue:#0072F5; --blue-dark:#0052B3; --ink:#0B0D10;
    --paper:#F6F4EF; --paper-dim:#ECE8DF; --line:rgba(11,13,16,0.12);
    background:var(--paper); color:var(--ink);
    font-family:'Inter',sans-serif; -webkit-font-smoothing:antialiased;
    min-height:100vh; display:flex; flex-direction:column;
  }
  .uc-root *{box-sizing:border-box;}
  .uc-root .serif{font-family:'Newsreader',serif; font-weight:400;}
  .uc-root a{color:inherit; text-decoration:none;}
  .uc-root .wrap{max-width:1240px; margin:0 auto; padding:0 32px;}
  .uc-root .measure{max-width:760px; margin:0 auto; padding:0 32px;}

  .uc-root header{
    position:fixed; top:0; left:0; right:0; z-index:90;
    background:rgba(246,244,239,0.92); backdrop-filter:blur(10px);
    border-bottom:1px solid var(--line);
  }
  .uc-root nav{display:flex; align-items:center; justify-content:space-between; padding:20px 32px; max-width:1240px; margin:0 auto;}
  .uc-root .logo{font-weight:800; font-size:18px; letter-spacing:-0.02em;}
  .uc-root .logo span{color:var(--blue);}
  .uc-root .nav-actions{display:flex; align-items:center; gap:22px;}
  .uc-root .nav-login{font-size:14px; font-weight:600; opacity:.75; transition:opacity .2s;}
  .uc-root .nav-login:hover{opacity:1;}
  .uc-root .nav-cta{font-size:14px; font-weight:600; padding:10px 20px; border:1px solid var(--ink); border-radius:2px; transition:background .2s,color .2s;}
  .uc-root .nav-cta:hover{background:var(--ink); color:var(--paper);}

  .uc-root main{flex:1;}

  .uc-root .uc-hero{padding:150px 0 20px;}
  .uc-root .eyebrow{
    font-size:13px; font-weight:600; letter-spacing:.12em; text-transform:uppercase;
    color:var(--blue-dark); margin-bottom:18px; display:flex; align-items:center; gap:10px;
  }
  .uc-root .eyebrow::before{content:''; width:7px; height:7px; background:var(--blue); border-radius:50%; display:inline-block;}
  .uc-root .uc-hero h1{
    font-size:clamp(30px, 4.6vw, 50px); line-height:1.06; letter-spacing:-0.02em; font-weight:800; margin:0 0 16px; max-width:720px;
  }
  .uc-root .uc-hero p{font-size:17px; line-height:1.6; color:#3a3d42; max-width:620px; margin:0;}

  /* body content — blurred once the gate appears */
  .uc-root .uc-body{padding-bottom:0; transition:filter .35s ease; }
  .uc-root .uc-body.gated{filter:blur(9px); user-select:none; pointer-events:none;}

  .uc-root .uc-grid{
    display:grid; grid-template-columns:repeat(3, 1fr); gap:22px;
    margin-top:40px;
  }
  .uc-root .uc-card{
    background:#fff; border-radius:16px; padding:26px 26px 22px;
    border:1px solid var(--line); display:flex; flex-direction:column;
  }
  .uc-root .uc-card-top{display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:10px;}
  .uc-root .uc-card h3{font-size:18px; font-weight:700; letter-spacing:-0.01em; margin:0; line-height:1.3;}
  .uc-root .uc-tag{
    flex-shrink:0; font-size:11px; font-weight:600; letter-spacing:.03em;
    background:var(--paper-dim); color:#52555a; padding:5px 10px; border-radius:999px; white-space:nowrap;
  }
  .uc-root .uc-card p.desc{font-size:14.5px; line-height:1.55; color:#52555a; margin:0 0 20px; flex:1;}
  .uc-root .uc-card-actions{display:flex; align-items:center; gap:8px; flex-wrap:wrap;}
  .uc-root .uc-copy-btn{
    background:var(--blue); color:#fff; border:none; cursor:pointer;
    font-size:12.5px; font-weight:700; letter-spacing:.04em; text-transform:uppercase;
    padding:11px 16px; border-radius:999px; transition:background .2s;
    display:inline-flex; align-items:center; gap:6px;
  }
  .uc-root .uc-copy-btn:hover{background:var(--blue-dark);}
  .uc-root .uc-copy-btn.copied{background:#16a34a;}
  .uc-root .uc-build-btn{
    border:1.5px solid var(--ink); color:var(--ink); background:transparent; cursor:pointer;
    font-size:12.5px; font-weight:700; padding:10px 16px; border-radius:999px;
    display:inline-flex; align-items:center; gap:6px; transition:background .2s, color .2s;
  }
  .uc-root .uc-build-btn:hover{background:var(--ink); color:#fff;}
  .uc-root .uc-expand-btn{
    width:34px; height:34px; border-radius:50%; border:1px solid var(--line); background:var(--paper);
    display:flex; align-items:center; justify-content:center; cursor:pointer; flex-shrink:0;
    transition:background .2s, transform .2s; margin-left:auto;
  }
  .uc-root .uc-expand-btn:hover{background:var(--paper-dim);}
  .uc-root .uc-expand-btn.open{transform:rotate(180deg);}
  .uc-root .uc-prompt-panel{
    margin-top:16px; padding-top:16px; border-top:1px solid var(--line);
    font-family:'Menlo',Monaco,Consolas,monospace; font-size:12.5px; line-height:1.6;
    color:#3a3d42; white-space:pre-wrap;
  }

  /* gate modal */
  .uc-gate-backdrop{
    position:fixed; inset:0; z-index:200;
    background:rgba(11,13,16,0.7);
    display:flex; align-items:center; justify-content:center;
    padding:24px; overflow-y:auto;
    animation:uc-fade .3s ease both;
  }
  @keyframes uc-fade{from{opacity:0;}to{opacity:1;}}
  @media (prefers-reduced-motion:reduce){.uc-gate-backdrop{animation:none;}}
  .uc-gate-card{
    background:#fff; border-radius:16px; max-width:440px; width:100%; min-width:0;
    padding:44px 36px 36px; text-align:center;
    box-shadow:0 30px 60px rgba(11,13,16,0.35);
    box-sizing:border-box;
  }
  .uc-gate-card .gate-logo{
    font-weight:800; font-size:19px; letter-spacing:-0.02em; color:var(--ink); margin-bottom:26px;
  }
  .uc-gate-card .gate-logo span{color:var(--blue);}
  .uc-gate-card h2{font-size:24px; font-weight:800; letter-spacing:-0.01em; margin:0 0 24px; line-height:1.25;}
  .uc-gate-card .gate-label{font-size:14px; color:#52555a; margin:0 0 12px; text-align:left;}
  .uc-gate-card input{
    width:100%; min-width:0; font-family:'Inter',sans-serif; font-size:15px; padding:14px 16px;
    border:1px solid var(--line); border-radius:999px; background:var(--paper); color:var(--ink);
    margin-bottom:16px; transition:border-color .2s;
  }
  .uc-gate-card input:focus{outline:none; border-color:var(--blue); background:#fff;}
  .uc-gate-card .gate-submit{
    width:100%; background:var(--blue); color:#fff; border:none; cursor:pointer;
    font-weight:700; font-size:15px; padding:15px 20px; border-radius:999px;
    transition:background .2s, opacity .2s;
  }
  .uc-gate-card .gate-submit:hover:not(:disabled){background:var(--blue-dark);}
  .uc-gate-card .gate-submit:disabled{opacity:.55; cursor:not-allowed;}
  .uc-gate-card .gate-terms{font-size:12.5px; color:#8a8d92; margin-top:18px; line-height:1.5;}
  .uc-gate-card .gate-terms a{color:#6b6e73; text-decoration:underline;}
  .uc-gate-card .gate-error{color:#c0392b; font-size:13px; margin:-6px 0 14px;}
  .uc-gate-card .gate-success{padding:6px 0 2px;}
  .uc-gate-card .gate-success .check{
    width:52px; height:52px; border-radius:50%; background:var(--blue); color:#fff;
    display:flex; align-items:center; justify-content:center; margin:0 auto 18px; font-size:24px;
  }

  /* dark closing CTA */
  .uc-cta{background:var(--ink); color:#fff; padding:110px 0; text-align:center; margin-top:70px;}
  .uc-cta h2{
    font-size:clamp(30px,5vw,52px); font-weight:800; letter-spacing:-0.02em; line-height:1.1; margin:0 0 20px;
  }
  .uc-cta h2 em{font-style:italic; font-family:'Newsreader',serif; color:#5b9dff; font-weight:500;}
  .uc-cta p{font-size:16px; color:#a8adb5; max-width:520px; margin:0 auto 36px; line-height:1.6;}
  .uc-cta .cta-actions{display:flex; align-items:center; justify-content:center; gap:14px; flex-wrap:wrap;}
  .uc-cta .cta-primary{
    background:var(--blue); color:#fff; font-weight:700; font-size:15px;
    padding:15px 28px; border-radius:999px; display:inline-flex; align-items:center; gap:8px;
    transition:background .2s;
  }
  .uc-cta .cta-primary:hover{background:var(--blue-dark);}
  .uc-cta .cta-secondary{
    border:1.5px solid rgba(255,255,255,0.25); color:#fff; font-weight:600; font-size:15px;
    padding:14px 26px; border-radius:999px; transition:border-color .2s, background .2s;
  }
  .uc-cta .cta-secondary:hover{background:rgba(255,255,255,0.08); border-color:rgba(255,255,255,0.4);}

  .uc-root footer{padding:64px 0 32px; border-top:1px solid var(--line);}
  .uc-root .foot-cols{display:grid; grid-template-columns:1.6fr 1fr 1fr 1fr; gap:40px; padding-bottom:48px; border-bottom:1px solid var(--line); margin-bottom:24px;}
  .uc-root .foot-brand p{font-size:14px; color:#6b6e73; margin-top:14px; max-width:260px; line-height:1.55;}
  .uc-root .foot-col-title{font-size:12px; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:#6b6e73; margin-bottom:16px;}
  .uc-root .foot-col a{display:block; font-size:14px; color:#52555a; padding:6px 0; transition:color .2s;}
  .uc-root .foot-col a:hover{color:var(--ink);}
  .uc-root .foot-bottom{font-size:13px; color:#6b6e73;}

  @media (max-width:1000px){
    .uc-root .uc-grid{grid-template-columns:repeat(2, 1fr);}
  }
  @media (max-width:860px){
    .uc-root nav{padding:16px 20px;}
    .uc-root .logo{font-size:16px;}
    .uc-root .nav-actions{gap:10px;}
    .uc-root .nav-login{font-size:13px;}
    .uc-root .nav-cta{font-size:13px; padding:8px 14px;}
    .uc-root .uc-hero{padding-top:150px;}
    .uc-root .wrap, .uc-root .measure{padding:0 20px;}
    .uc-root .uc-grid{grid-template-columns:1fr;}
    .uc-root .foot-cols{grid-template-columns:1fr; gap:32px;}
    .uc-gate-card{padding:36px 24px 28px;}
    .uc-cta{padding:80px 0;}
  }

  @media (max-width:420px){
    .uc-root .nav-login{display:none;}
  }
`;

const Chrome = ({ children }) => (
  <div className="uc-root">
    <style>{css}</style>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
    <link
      href="https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;1,6..72,400&family=Inter:wght@400;500;600;700;800&display=swap"
      rel="stylesheet"
    />
    <header>
      <nav>
        <Link to="/" className="logo">Data<span>Backfill</span></Link>
        <div className="nav-actions">
          <Link to="/blog" className="nav-login">Blog</Link>
          <Link to="/contact" className="nav-cta">Start a Project</Link>
        </div>
      </nav>
    </header>
    <main>{children}</main>
    <footer>
      <div className="wrap foot-cols">
        <div className="foot-brand">
          <div className="logo">Data<span>Backfill</span></div>
          <p>A boutique AI & data delivery studio: senior engineers, architects, and product designers shipping production systems.</p>
        </div>
        <div className="foot-col">
          <div className="foot-col-title">Services</div>
          <Link to="/#services">Claude Implementation</Link>
          <Link to="/#services">RAG & Knowledge Systems</Link>
          <Link to="/#services">Agentic Workflows</Link>
          <Link to="/#services">Data Infrastructure</Link>
        </div>
        <div className="foot-col">
          <div className="foot-col-title">Company</div>
          <Link to="/#work">Case Studies</Link>
          <Link to="/#team">Team</Link>
          <Link to="/blog">Blog</Link>
          <Link to="/contact">Contact</Link>
        </div>
        <div className="foot-col">
          <div className="foot-col-title">Use Cases</div>
          <Link to="/use-cases/cmo">25 Use Cases for CMOs</Link>
          <Link to="/use-cases/marketing-ai">25 Marketing AI Use Cases</Link>
          <Link to="/use-cases/ai-bdr">25 AI BDR Use Cases</Link>
          <Link to="/use-cases/cfo">15 Use Cases for CFOs</Link>
          <Link to="/use-cases/customer-support">20 AI Customer Support Use Cases</Link>
          <Link to="/use-cases/revops">12 RevOps AI Use Cases</Link>
          <Link to="/use-cases/hr">10 AI Use Cases for HR Teams</Link>
          <Link to="/use-cases/data-teams">18 Use Cases for Data Teams</Link>
        </div>
      </div>
      <div className="wrap foot-bottom">
        <div>© {new Date().getFullYear()} DataBackfill. All rights reserved.</div>
      </div>
    </footer>
  </div>
);

const GateModal = ({ list, onUnlock }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          email,
          formType: 'usecase-gate',
          category: list.slug,
          title: list.title,
          _subject: `New use-case unlock: ${list.title}`,
        }),
      });
      if (res.ok) {
        setStatus('success');
        try { sessionStorage.setItem(SESSION_PREFIX + list.slug, '1'); } catch { /* ignore */ }
        setTimeout(onUnlock, 700);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="uc-gate-backdrop" role="dialog" aria-modal="true" aria-label={`Unlock ${list.title}`}>
      <div className="uc-gate-card">
        {status === 'success' ? (
          <div className="gate-success">
            <div className="check">✓</div>
            <h2 style={{ marginBottom: 4 }}>You're in.</h2>
            <p style={{ fontSize: 14, color: '#52555a' }}>Unlocking the full list…</p>
          </div>
        ) : (
          <>
            <div className="gate-logo">Data<span>Backfill</span></div>
            <h2>{list.title}</h2>
            <form onSubmit={handleSubmit}>
              <p className="gate-label">Enter your email to access the page</p>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-label="Email address"
              />
              {status === 'error' && (
                <p className="gate-error">Something went wrong. Please try again.</p>
              )}
              <button type="submit" className="gate-submit" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Unlocking…' : 'Unlock full list'}
              </button>
            </form>
            <p className="gate-terms">
              By continuing, you agree to DataBackfill's{' '}
              <Link to="/terms">Terms of Service</Link> and{' '}
              <Link to="/privacypolicy">Privacy Policy</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const UseCaseCard = ({ item }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(item.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable; silently ignore */
    }
  };

  return (
    <div className="uc-card">
      <div className="uc-card-top">
        <h3>{item.title}</h3>
        <span className="uc-tag">{item.tag}</span>
      </div>
      <p className="desc">{item.description}</p>
      <div className="uc-card-actions">
        <button type="button" className={`uc-copy-btn${copied ? ' copied' : ''}`} onClick={handleCopy}>
          {copied ? 'Copied' : 'Copy prompt'}
        </button>
        <Link to="/contact" className="uc-build-btn">Build this →</Link>
        <button
          type="button"
          className={`uc-expand-btn${open ? ' open' : ''}`}
          aria-expanded={open}
          aria-label={open ? 'Hide prompt' : 'Show prompt'}
          onClick={() => setOpen((o) => !o)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {open && <div className="uc-prompt-panel">{item.prompt}</div>}
    </div>
  );
};

const UseCasePage = () => {
  const { slug } = useParams();
  const list = getUseCasesBySlug(slug);
  const [unlocked, setUnlocked] = useState(false);
  const [checkedSession, setCheckedSession] = useState(false);
  const [gateVisible, setGateVisible] = useState(false);

  useEffect(() => {
    if (!list) return undefined;
    let already = false;
    try { already = sessionStorage.getItem(SESSION_PREFIX + list.slug) === '1'; } catch { /* ignore */ }
    setUnlocked(already);
    setCheckedSession(true);

    if (already) return undefined;
    const timer = setTimeout(() => setGateVisible(true), GATE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [list]);

  useEffect(() => {
    const gated = list && checkedSession && !unlocked && gateVisible;
    document.body.style.overflow = gated ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [list, checkedSession, unlocked, gateVisible]);

  if (!list) {
    return (
      <Chrome>
        <div className="wrap" style={{ padding: '180px 0 120px', textAlign: 'center' }}>
          <h1 className="serif" style={{ fontSize: 34, marginBottom: 14 }}>List not found</h1>
          <p style={{ color: '#52555a', marginBottom: 28 }}>That use-case list doesn't exist or moved.</p>
          <Link to="/" className="nav-cta" style={{ display: 'inline-block' }}>Back home</Link>
        </div>
      </Chrome>
    );
  }

  const showGate = checkedSession && !unlocked && gateVisible;

  return (
    <Chrome>
      <div className={`uc-body ${showGate ? 'gated' : ''}`}>
        <section className="uc-hero measure">
          <div className="eyebrow">{list.eyebrow}</div>
          <h1>{list.title}</h1>
          <p>{list.subtitle}</p>
        </section>

        <section className="wrap">
          <div className="uc-grid">
            {list.items.map((item) => (
              <UseCaseCard item={item} key={item.title} />
            ))}
          </div>
        </section>

        <section className="uc-cta">
          <div className="wrap">
            <h2>Ready to build your<br /><em>{list.persona} agent stack?</em></h2>
            <p>Copy a prompt above, or skip straight to having us build it. Either way, a production agent beats a slide deck.</p>
            <div className="cta-actions">
              <Link to="/contact" className="cta-primary">
                Start a Project
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </Link>
              <a href="mailto:support@databackfill.com" className="cta-secondary">Talk to an Expert</a>
            </div>
          </div>
        </section>
      </div>

      {showGate && <GateModal list={list} onUnlock={() => setUnlocked(true)} />}
    </Chrome>
  );
};

export default UseCasePage;
